import { mdiCheck, mdiClose, mdiDelete, mdiPencil, mdiPlus } from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, ReactNode, useEffect, useState } from "react";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import { OrderItemModelV2 } from "../../../../../service/models/orders/order_item_model";
import {
  ItemModelV2,
  ItemModelV2Default,
} from "../../../../../erp/inventory/models/item_model";
import ItemSearchFieldV2 from "../../../../../erp/inventory/items/item_search_fieldv2";

const defaultItem: OrderItemModelV2 = {
  item_id: "",
  item: ItemModelV2Default,
  price: 0,
  qty: 0,
  delivered_qty: 0,
  installed_qty: 0,
  returned_qty: 0,
};

interface ProductRowWidgetPropsV2 {
  item?: OrderItemModelV2;
  onSubmit: (data: OrderItemModelV2) => void;
  edit: boolean;
  canEdit?: boolean;
  onDelete?: (i: OrderItemModelV2) => void;
  item_search_type?: string;
  item_search_subcategory?: string;
  isEditAllowed?: boolean;
  onItemChange?: (item: ItemModelV2) => void;
  availability?: ReactNode;
  id_prefix?: string;
}

// GST is always auto-applied from the item master's own tax slab — never
// user-editable here. Price entered in this row is always the base
// (ex-GST) amount; item.price stays the final GST-inclusive amount, exactly
// as every downstream consumer (approval, warehouse hand-off, totals) has
// always expected it to be — only how that number gets arrived at changes.
const getGstRate = (item?: ItemModelV2): number =>
  item?.tax_applicable ? (item?.tax_detail?.gst_rate ?? 0) : 0;

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

const ProductRowWidgetV2: FunctionComponent<ProductRowWidgetPropsV2> = (
  props,
) => {
  const { showToast } = useToast();
  const { edit, onSubmit, isEditAllowed = true } = props;
  const [item, setItem] = useState<OrderItemModelV2>({
    ...(props.item ?? defaultItem),
  });
  // Base (ex-GST) price backing the row. Seeded by reverse-deriving from an
  // existing item's final price so editing an existing order doesn't shift
  // its stored amount.
  const [basePrice, setBasePrice] = useState<number>(() => {
    const seed = props.item ?? defaultItem;
    const rate = getGstRate(seed.item);
    return seed.price ? round2(seed.price / (1 + rate / 100)) : 0;
  });
  const [editable, setEditable] = useState(false);
  const [isEditByLoginUser, setIsEditByLoginUser] = useState(false);
  const [uniqueKey] = useState(Math.random() * 1000);

  const gstRate = getGstRate(item.item);
  const finalPrice = round2(basePrice * (1 + gstRate / 100));

  useEffect(() => {
    setItem((prev) => (prev.price === finalPrice ? prev : { ...prev, price: finalPrice }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalPrice]);

  useEffect(() => {
    setIsEditByLoginUser(isEditAllowed);
  }, [isEditByLoginUser, isEditAllowed]);
  useEffect(() => {
    setEditable(edit);
  }, [edit]);

  const onDone = () => {
    if (!item.item_id || item.qty < 1) {
      showToast({
        type: ToastType.warning,
        text: "Please select product and Qty",
      });
      return;
    }
    // Compute directly rather than relying on the sync effect having already
    // flushed, so a save right after typing never submits a stale price.
    onSubmit({ ...item, price: finalPrice });
  };
  const canEdit = editable && isEditAllowed;

  console.log("editable:", editable, isEditAllowed, canEdit);

  // Stable, automation-friendly row id: existing rows key off the item code,
  // the trailing blank form row is always "new".
  const rowId = `${props.id_prefix ?? "order-item"}-row-${
    props.item ? (props.item.item?.item_code ?? props.item.item_id) : "new"
  }`;

  return (
    <>
      <form
        key={uniqueKey}
        id={rowId}
        onSubmit={(e) => {
          e.preventDefault();
          onDone();
        }}
        className="bg-white rounded-md shadow p-2 md:p-5"
      >
        <div className="grid grid-cols-2 md:grid-cols-9 gap-2 md:items-center ">
          <div className="flex flex-col gap-0.5 col-span-2 md:col-span-1">
            <span className="md:hidden text-gray-400 text-xs">Item Code</span>
            <input
              id={`${rowId}-item-code`}
              value={item?.item?.item_code}
              disabled={!editable}
              placeholder=""
              type="text"
              className="text-right p-1 rounded bg-gray-100 focus:outline-none w-full"
            />
          </div>
          <div className="flex flex-col gap-0.5 col-span-2 md:col-span-1">
            <span className="md:hidden text-gray-400 text-xs">Item Name</span>
            {props.item?.name ? (
              <input
                id={`${rowId}-item-name`}
                disabled={true}
                value={props.item.name}
                className="w-full"
              />
            ) : (
              <ItemSearchFieldV2
                id={`${rowId}-item-search`}
                value={props.item?.item}
                type={props.item_search_type}
                subcategory={props.item_search_subcategory}
                onSelect={function (data: ItemModelV2): void {
                  setItem((prev) => ({
                    ...prev,
                    item_id: data.id,
                    item: data,
                    created_at: Date.now(),
                  }));
                  props.onItemChange?.(data);
                }}
              />
            )}
          </div>

          <div className="flex flex-col gap-0.5 col-span-2 md:col-span-1">
            <span className="md:hidden text-gray-400 text-xs">Description</span>
            <input
              id={`${rowId}-description`}
              value={item?.item?.description}
              onChange={(e) => {
                setItem((prev) => ({
                  ...prev,
                  item: {
                    ...(prev.item ?? {}),
                    description: e.target.value,
                  } as ItemModelV2,
                }));
              }}
              disabled={true}
              placeholder=""
              type="text"
              className="text-right p-1 rounded bg-gray-100 focus:outline-none w-full"
            />
          </div>

          <div className="flex flex-col gap-0.5">
            <span className="md:hidden text-gray-400 text-xs">Quantity</span>
            <input
              id={`${rowId}-qty`}
              value={item?.qty === 0 ? "" : item?.qty}
              onChange={(e) => {
                let v = 0;
                if (e.target.value && !isNaN(e.target.value as any)) {
                  v = parseInt(e.target.value);
                }
                setItem((ol) => ({ ...ol, qty: v }));
              }}
              disabled={!canEdit}
              placeholder=""
              type="text"
              className="text-right p-1 rounded bg-gray-100 focus:outline-none w-full"
            />
          </div>

          <div className="flex flex-col gap-0.5">
            <span className="md:hidden text-gray-400 text-xs">
              Price per unit (excl. GST)
            </span>
            <input
              id={`${rowId}-price`}
              value={basePrice === 0 ? "" : basePrice}
              onChange={(e) => {
                const val = e.target.value;

                if (val === "") {
                  setBasePrice(0);
                  return;
                }

                const parsed = Number(val);
                if (!Number.isFinite(parsed)) return;

                setBasePrice(parsed);
              }}
              disabled={!canEdit}
              placeholder=""
              type="number"
              step="any" // allow decimals; or set step="0.01" for 2 decimal places
              className="text-right p-1 rounded bg-gray-100 focus:outline-none w-full"
            />
          </div>

          <div className="flex flex-col gap-0.5">
            <span className="md:hidden text-gray-400 text-xs">GST %</span>
            <input
              id={`${rowId}-gst-rate`}
              value={gstRate ? `${gstRate}%` : "-"}
              disabled={true}
              placeholder=""
              type="text"
              className="text-right p-1 rounded bg-gray-100 focus:outline-none w-full text-gray-500"
            />
          </div>

          <div className="flex flex-col gap-0.5">
            <span className="md:hidden text-gray-400 text-xs">
              Final Price (incl. GST)
            </span>
            <input
              id={`${rowId}-final-price`}
              value={finalPrice === 0 ? "" : finalPrice}
              disabled={true}
              placeholder=""
              type="text"
              className="text-right p-1 rounded bg-gray-100 focus:outline-none w-full"
            />
          </div>

          <div className="flex flex-col gap-0.5">
            <span className="md:hidden text-gray-400 text-xs">Amount</span>
            <input
              id={`${rowId}-amount`}
              value={(item?.qty ?? 0) * finalPrice}
              disabled={true}
              placeholder=""
              type="text"
              className="text-right p-1 rounded bg-gray-100 focus:outline-none w-full"
            />
          </div>

          {isEditByLoginUser && !props.canEdit && (
            <>
              {props.item ? (
                <div className="flex gap-2 items-center justify-evenly">
                  {editable ? (
                    <>
                      <div
                        id={`${rowId}-save-btn`}
                        onClick={() => {
                          if (!window.confirm("Are you sure !")) return;
                          onDone();
                          setEditable(false);
                        }}
                        className="bg-green-100 p-1 rounded text-green-800 hover:scale-110 transform duration-200"
                      >
                        <Icon path={mdiCheck} className="h-4 w-4" />
                      </div>
                      <div
                        id={`${rowId}-cancel-btn`}
                        onClick={() => {
                          setItem(props.item!);
                          setEditable(false);
                        }}
                        className="bg-red-100 p-1 rounded text-red-800 hover:scale-110 transform duration-200"
                      >
                        <Icon path={mdiClose} className="h-4 w-4" />
                      </div>
                    </>
                  ) : (
                    <div
                      id={`${rowId}-edit-btn`}
                      onClick={() => setEditable(true)}
                      className="bg-green-100 p-1 rounded text-green-800 hover:scale-110 transform duration-200"
                    >
                      <Icon path={mdiPencil} className="h-4 w-4" />
                    </div>
                  )}
                  <div
                    id={`${rowId}-delete-btn`}
                    onClick={() => {
                      if (!window.confirm("Are you sure !")) return;
                      if (props.onDelete) props.onDelete(item);
                    }}
                    className="bg-red-200 p-1 rounded text-red-800 hover:scale-110 transform duration-200"
                  >
                    <Icon path={mdiDelete} className="h-4 w-4" />
                  </div>
                </div>
              ) : (
                <button
                  id={`${rowId}-add-btn`}
                  className="ml-auto md:ml-0 flex items-center justify-center gap-1 text-white text-sm bg-myPrimaryColor rounded-full p-2 hover:scale-110 transform duration-300"
                >
                  <Icon path={mdiPlus} className="h-5 w-5" />
                  <span className="hidden md:inline">Add</span>
                </button>
              )}
            </>
          )}
        </div>
        {props.availability}
      </form>
    </>
  );
};

export default ProductRowWidgetV2;
